import { useEffect, useState, useCallback, useRef } from 'react';
import { planRowDate } from '../utils/planRowDate';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Flame, Beef, Wheat, Droplet, Zap, Calendar, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import CalendarView from '../components/CalendarView';
import MealCard from '../components/MealCard';
import JumpToPicker from '../components/JumpToPicker';
import useUserStore from '../store/userStore';
import { nutritionAPI, profileAPI, mealPlanAPI, mealAPI } from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  getWeekDatesWithOffset, 
  getWeekDatesArray,
  getWeekRangeText, 
  normalizeDate, 
  formatDateString, 
  getDayLabel,
  getTodayString 
} from '../utils/dateUtils';
import { num, clamp } from '../utils/num';
import { hasRealNutritionTargets } from '../utils/nutritionTargets';
import { getDayStatus } from './progressDayStatus';
import { isTimeoutError } from '../utils/requestLifecycle';
import NutritionTargetsSetupPrompt from '../components/NutritionTargetsSetupPrompt';
import { mealFromPlanRow } from '../utils/planRowMeal';

// Finite per-request timeout for every Progress load request (the shared
// api.js lifecycle retries a timed-out GET once — the only retry), plus an
// overall deadline for the whole load (covers native requests that ignore the
// axios timeout and the Daily meal-detail second phase).
export var PROGRESS_REQUEST_TIMEOUT_MS = 10000;
export var PROGRESS_LOAD_DEADLINE_MS = 25000;

// Only the latest (not cancelled) load may touch Progress state.
function isCurrentLoad(loadRef, load) {
  return !load.cancelled && loadRef.current === load;
}

// Race a load against the overall deadline; on expiry abort its requests and
// reject with a timeout error.
function withDeadline(load, promise) {
  var timer = new Promise(function(_resolve, reject) {
    load.deadline = setTimeout(function() {
      load.controller.abort();
      var err = new Error('Progress load timed out');
      err.code = 'ECONNABORTED';
      reject(err);
    }, PROGRESS_LOAD_DEADLINE_MS);
  });
  return Promise.race([promise, timer]);
}


function Progress() {
  var navigate = useNavigate();
  var [searchParams] = useSearchParams();
  var { profile, setProfile } = useUserStore();
  var [loading, setLoading] = useState(true);
  
  // Action loading state for meal cards
  var [actionLoading, setActionLoading] = useState({});
  
  // View mode: 'daily', 'weekly', 'monthly' - Default to Weekly
  var [viewMode, setViewMode] = useState(searchParams.get('view') || 'weekly');
  
  // Selected date for daily view
  var [selectedDate, setSelectedDate] = useState(searchParams.get('date') || getTodayString());
  
  // Week offset for weekly navigation
  var [weekOffset, setWeekOffset] = useState(0);
  
  // Month offset for monthly view
  var [monthOffset, setMonthOffset] = useState(0);
  
  // Week index within the month for monthly view graph (0-based). Starts at the
  // week containing today (the same value the monthOffset effect below sets), so
  // that effect is a no-op on mount and the initial load is not issued twice.
  var [monthWeekIndex, setMonthWeekIndex] = useState(function() { return getCurrentWeekIndexInMonth(0); });
  
  // SINGLE SOURCE OF TRUTH: Current week dates array (computed once, used everywhere)
  var [currentWeekDates, setCurrentWeekDates] = useState([]);
  
  // Daily view data
  var [dailyMeals, setDailyMeals] = useState([]);
  var [dailyMealDetails, setDailyMealDetails] = useState({});
  var [dailySummary, setDailySummary] = useState(null);
  
  // Weekly/Monthly trend data
  var [weeklyData, setWeeklyData] = useState([]);
  var [chartMacro, setChartMacro] = useState('calories');
  
  // Date picker popup state
  var [showDatePicker, setShowDatePicker] = useState(false);
  var [pickerMonth, setPickerMonth] = useState(new Date().getMonth());
  var [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  // Hover state for chart tooltips
  var [hoveredPoint, setHoveredPoint] = useState(null);

  var today = getTodayString();

  // Get all weeks within a given month (Sunday start) - uses shared utility internally
  function getWeeksInMonth(offset) {
    var now = normalizeDate(new Date());
    var targetMonth = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    var year = targetMonth.getFullYear();
    var month = targetMonth.getMonth();
    
    var weeks = [];
    var firstOfMonth = new Date(year, month, 1, 12, 0, 0);
    var lastOfMonth = new Date(year, month + 1, 0, 12, 0, 0);
    
    // Start from Sunday of the week containing the 1st
    var weekStart = new Date(firstOfMonth);
    var dayOfWeek = firstOfMonth.getDay(); // 0 = Sunday
    weekStart.setDate(firstOfMonth.getDate() - dayOfWeek);
    
    var weekNum = 1;
    while (weekStart <= lastOfMonth) {
      var weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      var dates = [];
      for (var i = 0; i < 7; i++) {
        var d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        dates.push(formatDateString(d));
      }
      
      weeks.push({
        weekNumber: weekNum,
        startDate: new Date(weekStart),
        endDate: weekEnd,
        dates: dates
      });
      
      weekStart.setDate(weekStart.getDate() + 7);
      weekNum++;
    }
    
    return weeks;
  }


  // Calculate weekly summary
  function calculateWeeklySummary(weekData, targets) {
    var met = 0;
    var partial = 0;
    var missed = 0;
    var macroDeltas = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    var daysWithData = 0;
    
    for (var i = 0; i < weekData.length; i++) {
      var day = weekData[i];
      if (day && num(day.calories) > 0) {
        daysWithData++;
        var status = getDayStatus(day, targets);
        if (status === 'met') met++;
        else if (status === 'partial') partial++;
        else if (status === 'missed') missed++;
        
        macroDeltas.calories += num(day.calories) - num(targets.calories);
        macroDeltas.protein += num(day.protein) - num(targets.protein);
        macroDeltas.carbs += num(day.carbs) - num(targets.carbs);
        macroDeltas.fat += num(day.fat) - num(targets.fat);
      }
    }
    
    if (daysWithData > 0) {
      macroDeltas.calories = Math.round(macroDeltas.calories / daysWithData);
      macroDeltas.protein = Math.round(macroDeltas.protein / daysWithData);
      macroDeltas.carbs = Math.round(macroDeltas.carbs / daysWithData);
      macroDeltas.fat = Math.round(macroDeltas.fat / daysWithData);
    }
    
    var biggestGap = { macro: 'calories', delta: macroDeltas.calories };
    if (Math.abs(macroDeltas.protein) > Math.abs(biggestGap.delta)) {
      biggestGap = { macro: 'protein', delta: macroDeltas.protein };
    }
    if (Math.abs(macroDeltas.carbs) > Math.abs(biggestGap.delta)) {
      biggestGap = { macro: 'carbs', delta: macroDeltas.carbs };
    }
    if (Math.abs(macroDeltas.fat) > Math.abs(biggestGap.delta)) {
      biggestGap = { macro: 'fat', delta: macroDeltas.fat };
    }
    
    return { met, partial, missed, biggestGap, daysWithData };
  }

  // Generate actionable insight (single, no duplicates)
  function generateInsight(summary) {
    if (!summary || summary.daysWithData === 0) {
      return "Log some meals to see personalized insights.";
    }
    
    var macro = summary.biggestGap.macro;
    var delta = summary.biggestGap.delta;
    var direction = delta < 0 ? 'under' : 'over';
    var absVal = Math.abs(delta);
    
    if (absVal < 10 && macro !== 'calories') {
      return "Great consistency! Your macros are well balanced this week.";
    }
    
    if (macro === 'calories') {
      if (direction === 'under') {
        return "You're averaging " + absVal + " cal under target. Consider adding a small snack between meals.";
      } else {
        return "You're averaging " + absVal + " cal over target. Watch portion sizes at dinner.";
      }
    } else if (macro === 'protein') {
      if (direction === 'under') {
        return "Protein is low (avg -" + absVal + "g/day). Try adding eggs or Greek yogurt to breakfast.";
      } else {
        return "Protein is high (avg +" + absVal + "g/day). This is generally fine unless you have kidney concerns.";
      }
    } else if (macro === 'carbs') {
      if (direction === 'under') {
        return "Carbs are low (avg -" + absVal + "g/day). Try adding more whole grains to your meals.";
      } else {
        return "Carbs are high (avg +" + absVal + "g/day). Consider reducing bread and rice portions.";
      }
    } else if (macro === 'fat') {
      if (direction === 'under') {
        return "Fat intake is low (avg -" + absVal + "g/day). Add healthy fats like avocado or nuts.";
      } else {
        return "Fat is high (avg +" + absVal + "g/day). Swap fried foods for grilled options.";
      }
    }
    
    return "Keep tracking to see more personalized insights.";
  }

  // Check if a specific action is loading for a plan
  function isActionLoading(planId, action) {
    return actionLoading[planId + '_' + action] === true;
  }

  // Update meal status (log, undo, skip)
  function updateMealStatus(planId, status, event) {
    if (event) event.stopPropagation();
    
    var action = status === 'completed' ? 'log' : status === 'pending' ? 'undo' : 'skip';
    setActionLoading(function(prev) {
      var newState = Object.assign({}, prev);
      newState[planId + '_' + action] = true;
      return newState;
    });
    
    mealPlanAPI.updateMealPlanStatus(planId, status)
      .then(function() {
        // Refresh the data for selected date
        fetchDailyData(selectedDate);
        if (status === 'completed') {
          toast.success('Meal logged!');
        } else if (status === 'pending') {
          toast.success('Meal unlogged');
        } else if (status === 'skipped') {
          toast.success('Meal skipped');
        }
      })
      .catch(function(err) {
        console.error('Failed to update status:', err);
        toast.error('Failed to update meal status');
      })
      .finally(function() {
        setActionLoading(function(prev) {
          var newState = Object.assign({}, prev);
          newState[planId + '_' + action] = false;
          return newState;
        });
      });
  }

  // Navigate to meal details — routes based on what the item actually IS, not just ID prefix
  function handleMealClick(mealId, planId, planItem) {
    // Admin-generated meals with structured USDA ingredients → MealView
    if (mealId?.startsWith('meal_v2_')) {
      var v2Params = new URLSearchParams();
      v2Params.set('return', '/progress');
      v2Params.set('date', planRowDate(planItem, selectedDate));
      if (planId) v2Params.set('planId', planId);
      if (planItem?.status) v2Params.set('status', planItem.status);
      if (planItem?.meal_type) v2Params.set('type', planItem.meal_type);
      // Pass scale factor for scaled meals
      if (planItem?.scale_factor && planItem.scale_factor !== 1.0) {
        v2Params.set('scaleFactor', planItem.scale_factor);
        if (planItem.scale_label) v2Params.set('scaleLabel', planItem.scale_label);
      }
      navigate('/meal-view/' + mealId + '?' + v2Params.toString());
      return;
    }
    
    // Determine if this is a single-food log or a multi-ingredient custom meal
    var ingredientCount = planItem?.ingredient_count || 0;
    var hasCustomIngredients = (planItem?.custom_ingredients?.length > 1);
    var hasIngredientDetails = (planItem?.ingredient_details?.length > 1);
    var isMultiIngredient = ingredientCount > 1 || hasCustomIngredients || hasIngredientDetails;
    
    // AI photo logs + multi-food basket logs are grouped meals (same rule as Home), so their
    // detail — and its Log Again — keeps the saved photo marker and component rows.
    var isGroupedLog = planItem?.source === 'AI_PHOTO_LOG' ||
      mealId?.startsWith('ailog_group_') || mealId?.startsWith('grouped_');

    // Multi-ingredient custom meals → CustomMealView (editable)
    if (isGroupedLog || (isMultiIngredient && (mealId?.startsWith('custom_') || mealId?.startsWith('meal_')))) {
      var params = new URLSearchParams();
      params.set('return', '/progress');
      params.set('date', planRowDate(planItem, selectedDate));
      if (planId) params.set('planId', planId);
      if (planItem?.status) params.set('status', planItem.status);
      if (planItem?.meal_type) params.set('type', planItem.meal_type);
      if (planItem?.meal_name) params.set('name', planItem.meal_name);
      if (planItem?.macros) {
        params.set('calories', planItem.macros.calories || 0);
        params.set('protein', planItem.macros.protein || 0);
        params.set('carbs', planItem.macros.carbs || 0);
        params.set('fat', planItem.macros.fat || 0);
      }
      navigate('/meal-view/' + mealId + '?' + params.toString() + '&view=grouped', { state: { grouped: true } });
      return;
    }
    
    // Single foods (branded, barcode, single-ingredient custom, USDA search) → MealView (read-only)
    // Pass plan data as URL params so the view has macros even if getMeal fails
    var mvParams = new URLSearchParams();
    mvParams.set('return', '/progress');
    mvParams.set('date', planRowDate(planItem, selectedDate));
    if (planId) mvParams.set('planId', planId);
    if (planItem?.status) mvParams.set('status', planItem.status);
    if (planItem?.meal_type) mvParams.set('type', planItem.meal_type);
    if (planItem?.meal_name) mvParams.set('name', planItem.meal_name);
    if (planItem?.macros) {
      mvParams.set('calories', planItem.macros.calories || 0);
      mvParams.set('protein', planItem.macros.protein || 0);
      mvParams.set('carbs', planItem.macros.carbs || 0);
      mvParams.set('fat', planItem.macros.fat || 0);
    }
    navigate('/meal-view/' + mealId + '?' + mvParams.toString());
  }

  // Remove meal from plan
  function removeMeal(planId, event) {
    if (event) event.stopPropagation();
    
    setActionLoading(function(prev) {
      var newState = Object.assign({}, prev);
      newState[planId + '_remove'] = true;
      return newState;
    });
    
    mealPlanAPI.removeMealPlan(planId)
      .then(function() {
        fetchDailyData(selectedDate);
        toast.success('Meal removed');
      })
      .catch(function(err) {
        console.error('Failed to remove meal:', err);
        toast.error('Failed to remove meal');
      })
      .finally(function() {
        setActionLoading(function(prev) {
          var newState = Object.assign({}, prev);
          newState[planId + '_remove'] = false;
          return newState;
        });
      });
  }

  // Add Meal: navigate directly to Log Food page
  function handleAddMealClick() {
    navigate('/log-food?date=' + selectedDate + '&return=/progress?view=daily%26date=' + selectedDate);
  }

  // ---- Load lifecycle (Daily / Weekly / Monthly) ----
  // Every Progress load gets finite request timeouts + an AbortController via
  // the existing api.js requestLifecycle(opts), plus an overall deadline so a
  // native request that never settles can't leave the page on an eternal
  // spinner. Only the LATEST load may touch state: a newer view/date/week
  // selection (or unmount) cancels the older one, so it can never overwrite
  // the newer selection or flip `loading` back.
  var loadRef = useRef(null);
  var [loadError, setLoadError] = useState(null);
  // Bumped by Retry to rerun the CURRENT view/date/week.
  var [reloadKey, setReloadKey] = useState(0);

  var cancelActiveLoad = useCallback(function() {
    var active = loadRef.current;
    if (active) {
      active.cancelled = true;
      active.controller.abort();
      if (active.deadline) clearTimeout(active.deadline);
    }
    loadRef.current = null;
  }, []);

  var beginLoad = useCallback(function() {
    cancelActiveLoad();
    var controller = new AbortController();
    var load = {
      controller: controller,
      cancelled: false,
      deadline: null,
      opts: { signal: controller.signal, timeout: PROGRESS_REQUEST_TIMEOUT_MS },
    };
    loadRef.current = load;
    setLoadError(null);
    setLoading(true);
    return load;
  }, [cancelActiveLoad]);

  var settleLoad = useCallback(function(load, err) {
    if (load.deadline) clearTimeout(load.deadline);
    if (!isCurrentLoad(loadRef, load)) return;  // superseded/unmounted: never touch state
    loadRef.current = null;
    if (err) setLoadError(err);
    setLoading(false);
  }, []);

  // Abort any in-flight load on unmount.
  useEffect(function() {
    return cancelActiveLoad;
  }, [cancelActiveLoad]);

  // Fetch profile data
  var fetchProfile = useCallback(function(opts) {
    return profileAPI.getProfile(opts)
      .then(function(res) {
        return res.data;
      });
  }, []);

  // Fetch daily data
  var fetchDailyData = useCallback(function(date) {
    var load = beginLoad();
    var opts = load.opts;

    withDeadline(load, Promise.all([
      mealPlanAPI.getDailyPlan(date, opts),
      nutritionAPI.getDailySummary(date, opts),
      fetchProfile(opts)
    ])
    .then(function(results) {
      if (!isCurrentLoad(loadRef, load)) return;
      var meals = results[0].data || [];
      setProfile(results[2]);
      setDailyMeals(meals);
      setDailySummary(results[1].data);
      
      var uniqueIds = [];
      var seen = {};
      for (var i = 0; i < meals.length; i++) {
        var id = meals[i].meal_id;
        if (id && !seen[id]) {
          seen[id] = true;
          uniqueIds.push(id);
        }
      }
      
      if (uniqueIds.length > 0) {
        var fetches = uniqueIds.map(function(id) {
          return mealAPI.getMeal(id, opts).catch(function() { return null; });
        });
        
        return Promise.all(fetches).then(function(mealResults) {
          if (!isCurrentLoad(loadRef, load)) return;
          var details = {};
          for (var j = 0; j < mealResults.length; j++) {
            if (mealResults[j]) {
              details[uniqueIds[j]] = mealResults[j].data;
            }
          }
          setDailyMealDetails(details);
        });
      }
    }))
    .then(function() {
      settleLoad(load, null);
    })
    .catch(function(err) {
      if (isCurrentLoad(loadRef, load)) console.error('[Progress] Daily fetch error:', err);
      settleLoad(load, err);
    });
  }, [beginLoad, settleLoad, fetchProfile, setProfile]);

  // Fetch weekly trend data (Weekly view, and Monthly's selected week)
  var fetchWeeklyData = useCallback(function(dates) {
    var load = beginLoad();
    var opts = load.opts;
    console.log('[Progress] Fetching weekly data for dates:', dates);
    var failedDays = 0;
    
    withDeadline(load, Promise.all([
      fetchProfile(opts),
      ...dates.map(function(d) {
        return nutritionAPI.getDailySummary(d, opts).catch(function() {
          failedDays += 1;
          return { data: null };
        });
      })
    ])
    .then(function(results) {
      if (!isCurrentLoad(loadRef, load)) return;
      // Individual missing days still render (as empty), but if EVERY day
      // failed there is nothing real to show — surface the error instead of
      // a silent all-zero week.
      if (dates.length > 0 && failedDays === dates.length) {
        throw new Error('All daily summaries failed to load');
      }
      var profileData = results[0];
      var dailyResults = results.slice(1);
      setProfile(profileData);
      
      // Progress page ONLY shows CONSUMED (logged) data
      // Planned meals belong on the Plan page, not Progress
      var data = dailyResults.map(function(r, idx) {
        var consumed = r.data?.consumed || {};
        
        return {
          date: dates[idx],
          calories: consumed.calories || 0,
          protein: consumed.protein || 0,
          carbs: consumed.carbs || 0,
          fat: consumed.fat || 0
        };
      });
      
      setWeeklyData(data);
    }))
    .then(function() {
      settleLoad(load, null);
    })
    .catch(function(err) {
      if (isCurrentLoad(loadRef, load)) console.error('[Progress] Weekly fetch error:', err);
      settleLoad(load, err);
    });
  }, [beginLoad, settleLoad, fetchProfile, setProfile]);

  // Helper function to find which week index contains today's date
  function getCurrentWeekIndexInMonth(offset) {
    if (offset !== 0) return 0; // Only auto-select for current month
    
    var weeks = getWeeksInMonth(0);
    var todayStr = getTodayString();
    
    for (var i = 0; i < weeks.length; i++) {
      if (weeks[i].dates.indexOf(todayStr) !== -1) {
        return i;
      }
    }
    return 0; // Fallback to first week
  }

  // Handle view mode change and data fetching
  // SINGLE SOURCE OF TRUTH: Compute dates ONCE using shared utility, use everywhere
  useEffect(function() {
    if (viewMode === 'daily') {
      fetchDailyData(selectedDate);
      setCurrentWeekDates([]); // Not used in daily view
    } else if (viewMode === 'weekly') {
      // Use shared utility for week calculation (Sunday start with timezone safeguard)
      var dates = getWeekDatesWithOffset(weekOffset);
      console.log('[Progress] WEEKLY SOURCE OF TRUTH - weekOffset:', weekOffset, 'dates:', dates);
      setCurrentWeekDates(dates);
      fetchWeeklyData(dates);
    } else if (viewMode === 'monthly') {
      var weeks = getWeeksInMonth(monthOffset);
      // Initialize monthWeekIndex to current week if not set (-1)
      var weekIdx = monthWeekIndex;
      if (weekIdx === -1) {
        weekIdx = getCurrentWeekIndexInMonth(monthOffset);
        setMonthWeekIndex(weekIdx);
      }
      var safeIndex = Math.min(weekIdx, weeks.length - 1);
      if (safeIndex < 0) safeIndex = 0;
      if (weeks[safeIndex]) {
        var monthDates = weeks[safeIndex].dates;
        console.log('[Progress] MONTHLY SOURCE OF TRUTH - monthOffset:', monthOffset, 'weekIndex:', safeIndex, 'dates:', monthDates);
        setCurrentWeekDates(monthDates);
        fetchWeeklyData(monthDates);
      }
    }
  }, [viewMode, selectedDate, weekOffset, monthOffset, monthWeekIndex, fetchDailyData, fetchWeeklyData, reloadKey]);

  // Reset monthWeekIndex when monthOffset changes - default to current week for current month
  useEffect(function() {
    var currentWeekIdx = getCurrentWeekIndexInMonth(monthOffset);
    setMonthWeekIndex(currentWeekIdx);
  }, [monthOffset]);

  // Handle calendar day click: every date (future ones included) opens its
  // Daily view right here in Progress — selecting a date never jumps the user
  // into Plan (the same Daily view the date picker already shows for any day).
  var handleCalendarDateSelect = function(dateStr) {
    setSelectedDate(dateStr);
    setViewMode('daily');
  };

  // Navigation helpers
  var returnToToday = function() {
    setSelectedDate(today);
    setWeekOffset(0);
    setMonthOffset(0);
    setMonthWeekIndex(0);
  };

  var returnToThisWeek = function() {
    setWeekOffset(0);
  };

  var returnToThisMonth = function() {
    setMonthOffset(0);
    setMonthWeekIndex(0);
  };

  // Check if we're viewing current period
  var isViewingToday = selectedDate === today;
  var isViewingThisWeek = weekOffset === 0;
  var isViewingThisMonth = monthOffset === 0;

  // Date picker handlers
  var handleDatePickerSelect = function(date) {
    if (viewMode === 'daily') {
      setSelectedDate(date);
    } else if (viewMode === 'weekly') {
      // Find which week offset this date corresponds to
      var targetDates = getWeekDatesArray(new Date(date + 'T00:00:00'));
      var currentDates = getWeekDatesWithOffset(0);
      var diffDays = Math.floor((new Date(targetDates[0]) - new Date(currentDates[0])) / (1000 * 60 * 60 * 24));
      var newOffset = Math.floor(diffDays / 7);
      setWeekOffset(newOffset);
    } else if (viewMode === 'monthly') {
      var targetDate = new Date(date + 'T00:00:00');
      var now = new Date();
      var monthDiff = (targetDate.getFullYear() - now.getFullYear()) * 12 + (targetDate.getMonth() - now.getMonth());
      setMonthOffset(monthDiff);
    }
    setShowDatePicker(false);
  };

  // Render calendar day content with status indicator
  var renderDayContent = function(dateStr) {
    // Check if this is a future date
    var dateObj = new Date(dateStr + 'T00:00:00');
    var todayObj = new Date(today + 'T00:00:00');
    
    if (dateObj > todayObj) {
      // Future date - no indicator (will route to planning when clicked)
      return null;
    }
    
    var dayData = weeklyData.find(function(d) { return d.date === dateStr; });
    var status = getDayStatus(dayData, profile);
    
    if (!status) return null;
    
    // Color based on macro achievement:
    // Green = met all targets, Yellow = partial (2-3 macros), Red = missed (0-1 macros)
    var statusColor = status === 'met' ? 'bg-green-500' : status === 'partial' ? 'bg-amber-500' : 'bg-red-500';
    
    return (
      <div className="flex justify-center mt-1">
        <div className={'w-2 h-2 rounded-full ' + statusColor}></div>
      </div>
    );
  };

  // Render line chart with improvements
  function renderLineChart() {
    if (weeklyData.length === 0) return null;
    
    // DEBUG: Log chart data dates
    console.log('[Progress] CHART rendering with weeklyData dates:', weeklyData.map(function(d) { return d.date; }));
    console.log('[Progress] CHART using currentWeekDates:', currentWeekDates);
    
    // Real data points always render regardless of target availability — only
    // the "Goal line" reference (drawn below) is hidden when there's no real,
    // personalized target, so it's never presented as if it were the user's own.
    var targetsAvailable = hasRealNutritionTargets(profile);
    var target = targetsAvailable ? num(profile[chartMacro]) : 0;
    var maxVal = targetsAvailable ? target * 1.3 : 1;
    
    // Find max value in data
    for (var i = 0; i < weeklyData.length; i++) {
      var val = num(weeklyData[i][chartMacro]);
      if (val > maxVal) maxVal = val * 1.1;
    }
    if (!(maxVal > 0)) maxVal = 1; // never divide by zero/NaN
    
    var chartHeight = 100;
    var chartWidth = 100;
    var numPoints = weeklyData.length;
    
    // Build points with colors - calculate percentage positions for both SVG and HTML
    var points = [];
    var segments = [];
    
    for (var j = 0; j < numPoints; j++) {
      var dataVal = num(weeklyData[j][chartMacro]);
      // Calculate x as percentage (0-100) - evenly distributed (guard single point)
      var xPct = numPoints > 1 ? (j / (numPoints - 1)) * 100 : 0;
      var yPct = clamp((1 - dataVal / maxVal) * 100, 0, 100);
      
      // Always use accent blue color for all points and lines
      var color = '#14D5F6'; // Accent blue - consistent for all data points
      
      points.push({ 
        xPct, 
        yPct, 
        x: xPct, // for SVG viewBox 0-100
        y: yPct, // for SVG viewBox 0-100
        value: dataVal, 
        date: weeklyData[j].date, 
        color 
      });
      
      // Build line segments between points
      if (j > 0) {
        segments.push({
          x1: points[j - 1].x,
          y1: points[j - 1].y,
          x2: xPct,
          y2: yPct,
          color: '#14D5F6' // Accent blue - consistent for all lines
        });
      }
    }
    
    var goalYPct = clamp((1 - target / maxVal) * 100, 0, 100);
    var unit = chartMacro === 'calories' ? '' : 'g';
    
    return (
      <div className="relative">
        {/* Chart Header */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
            {chartMacro === 'calories' ? 'Calorie' : chartMacro.charAt(0).toUpperCase() + chartMacro.slice(1)} Trend
          </p>
        </div>
        
        {/* Chart Container with consistent margins */}
        <div className="relative" style={{ height: '120px', marginLeft: '25px', marginRight: '10px' }}>
          {/* SVG Chart - lines only */}
          <svg 
            viewBox="0 0 100 100" 
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="none"
          >
            {/* Goal line — only drawn when the user has a real, personalized target */}
            {targetsAvailable && (
              <line
                x1="0" y1={goalYPct} x2="100" y2={goalYPct}
                stroke="#22c55e" strokeWidth="0.3" strokeDasharray="1.5,1.5"
              />
            )}

            {/* Line segments */}
            {segments.map(function(seg, idx) {
              return (
                <line
                  key={'seg-' + idx}
                  x1={seg.x1}
                  y1={seg.y1}
                  x2={seg.x2}
                  y2={seg.y2}
                  stroke={seg.color}
                  strokeWidth="0.8"
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          
          {/* Data points as HTML elements for perfect circles */}
          {points.map(function(p, idx) {
            var size = hoveredPoint === idx ? 8 : 6;
            return (
              <div
                key={idx}
                className="absolute rounded-full cursor-pointer transition-all duration-150"
                style={{
                  left: p.xPct + '%',
                  top: p.yPct + '%',
                  width: size + 'px',
                  height: size + 'px',
                  backgroundColor: p.color,
                  transform: 'translate(-50%, -50%)'
                }}
                onMouseEnter={function() { setHoveredPoint(idx); }}
                onMouseLeave={function() { setHoveredPoint(null); }}
              />
            );
          })}
          
          {/* Goal value label — only when the target itself is real */}
          {targetsAvailable && (
            <div
              className="absolute text-[9px] text-green-500 font-medium bg-background/90 px-1 rounded z-10"
              style={{ left: '-25px', top: goalYPct + '%', transform: 'translateY(-50%)' }}
            >
              {target}{unit}
            </div>
          )}

          {/* Tooltip on hover */}
          <AnimatePresence>
            {hoveredPoint !== null && points[hoveredPoint] && (
              <motion.div
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="absolute z-20 bg-card border border-border shadow-lg rounded-lg px-2 py-1 text-xs pointer-events-none"
                style={{ 
                  left: points[hoveredPoint].xPct + '%', 
                  top: points[hoveredPoint].yPct + '%',
                  transform: 'translate(-50%, -120%)'
                }}
              >
                <span className="font-semibold">{points[hoveredPoint].value}{unit}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* X-axis labels - uses currentWeekDates as SINGLE SOURCE OF TRUTH */}
        <div className="relative h-8 mt-1" style={{ marginLeft: '25px', marginRight: '10px' }}>
          {currentWeekDates.map(function(dateStr, idx) {
            // Use shared utility for day labels to ensure consistency
            var dayInfo = getDayLabel(dateStr);
            var leftPct = (idx / (currentWeekDates.length - 1)) * 100;
            
            return (
              <div 
                key={dateStr}
                className="absolute text-center"
                style={{ 
                  left: leftPct + '%', 
                  transform: 'translateX(-50%)',
                  width: '36px'
                }}
                onMouseEnter={function() { setHoveredPoint(idx); }}
                onMouseLeave={function() { setHoveredPoint(null); }}
              >
                <span className={'block text-[10px] ' + (hoveredPoint === idx ? 'text-foreground font-medium' : 'text-muted-foreground')}>{dayInfo.day}</span>
                <span className={'block text-[9px] ' + (hoveredPoint === idx ? 'text-foreground' : 'text-muted-foreground')}>{dayInfo.date}</span>
              </div>
            );
          })}
        </div>
        
        {/* Macro Toggle */}
        <div className="flex items-center justify-end mt-2">
          <div className="flex bg-secondary/60 rounded-full p-0.5">
            {[
              { key: 'calories', label: 'Cal' },
              { key: 'protein', label: 'Protein' },
              { key: 'carbs', label: 'Carbs' },
              { key: 'fat', label: 'Fat' }
            ].map(function(macro) {
              return (
                <button
                  key={macro.key}
                  onClick={function() { setChartMacro(macro.key); }}
                  className={'px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ' + 
                    (chartMacro === macro.key ? 'bg-accent text-white' : 'text-muted-foreground hover:text-foreground')}
                  data-testid={'macro-' + macro.key}
                >
                  {macro.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Render weekly summary (On Track / Under / Over counts + single insight)
  function renderWeeklySummary() {
    if (!hasRealNutritionTargets(profile)) {
      return <NutritionTargetsSetupPrompt />;
    }
    var targets = profile;
    var summary = calculateWeeklySummary(weeklyData, targets);
    var insight = generateInsight(summary);
    
    return (
      <div className="space-y-3">
        {/* Days Summary */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-green-500">{summary.met}</p>
            <p className="text-[10px] text-muted-foreground">Goals Met</p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-amber-500">{summary.partial}</p>
            <p className="text-[10px] text-muted-foreground">Partial</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-red-500">{summary.missed}</p>
            <p className="text-[10px] text-muted-foreground">Missed</p>
          </div>
        </div>
        
        {/* Single Actionable Insight - no duplicates */}
        <div className="bg-accent/5 border border-accent/20 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Zap size={14} className="text-accent" />
            </div>
            <p className="text-xs leading-relaxed">{insight}</p>
          </div>
        </div>
      </div>
    );
  }

  function renderDatePicker() {
    if (!showDatePicker) return null;
    
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var daysInMonth = new Date(pickerYear, pickerMonth + 1, 0).getDate();
    var firstDayOfWeek = new Date(pickerYear, pickerMonth, 1).getDay(); // 0 = Sunday (no adjustment needed)
    
    var days = [];
    for (var i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    for (var d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }
    
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={function() { setShowDatePicker(false); }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border border-border rounded-2xl p-4 w-full max-w-xs"
            onClick={function(e) { e.stopPropagation(); }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={function() {
                  if (pickerMonth === 0) {
                    setPickerMonth(11);
                    setPickerYear(pickerYear - 1);
                  } else {
                    setPickerMonth(pickerMonth - 1);
                  }
                }}
                className="p-1.5 rounded-full hover:bg-secondary"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold text-sm">{months[pickerMonth]} {pickerYear}</span>
              <button
                onClick={function() {
                  if (pickerMonth === 11) {
                    setPickerMonth(0);
                    setPickerYear(pickerYear + 1);
                  } else {
                    setPickerMonth(pickerMonth + 1);
                  }
                }}
                className="p-1.5 rounded-full hover:bg-secondary"
              >
                <ChevronRight size={18} />
              </button>
            </div>
            
            {/* Day headers - Sunday start */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(function(d, i) {
                return (
                  <div key={i} className="text-center text-[10px] text-muted-foreground font-medium py-1">
                    {d}
                  </div>
                );
              })}
            </div>
            
            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(function(day, idx) {
                if (!day) {
                  return <div key={'empty-' + idx} className="py-2"></div>;
                }
                var dateStr = pickerYear + '-' + String(pickerMonth + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
                var isToday = dateStr === today;
                var isSelected = dateStr === selectedDate;
                
                return (
                  <button
                    key={dateStr}
                    onClick={function() { handleDatePickerSelect(dateStr); }}
                    className={'py-2 text-xs rounded-lg transition-all ' + 
                      (isSelected ? 'bg-accent text-white' : 
                       isToday ? 'bg-accent/20 text-accent font-medium' : 
                       'hover:bg-secondary')}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            
            {/* Quick actions */}
            <div className="mt-4 pt-3 border-t border-border/40 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs h-8"
                onClick={function() {
                  handleDatePickerSelect(today);
                }}
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={function() { setShowDatePicker(false); }}
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Render daily view content (NO chart)
  function renderDailyView() {
    var targetsAvailable = hasRealNutritionTargets(profile);
    var targets = targetsAvailable ? profile : { calories: 0, protein: 0, carbs: 0, fat: 0 };

    // Calculate logged macros directly from completed meals for accuracy
    // This ensures we're summing from the actual logged meal data
    var loggedFromMeals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    var completed = [];
    var pending = [];
    for (var i = 0; i < dailyMeals.length; i++) {
      var m = dailyMeals[i];
      if (m.status === 'completed') {
        completed.push(m);
        // Sum macros from completed meals
        var mealMacros = m.macros || dailyMealDetails[m.meal_id] || {};
        loggedFromMeals.calories += mealMacros.calories || 0;
        loggedFromMeals.protein += mealMacros.protein || 0;
        loggedFromMeals.carbs += mealMacros.carbs || 0;
        loggedFromMeals.fat += mealMacros.fat || 0;
      } else if (m.status === 'pending') {
        pending.push(m);
      }
    }
    
    // Use API summary if available, otherwise use calculated values
    var logged = dailySummary?.consumed || loggedFromMeals;
    // If API returned 0s but we have calculated values, prefer calculated
    if (logged.calories === 0 && loggedFromMeals.calories > 0) {
      logged = loggedFromMeals;
    }
    
    // Determine if this is a past date (for Add Meal placement)
    var selectedDateObj = new Date(selectedDate + 'T00:00:00');
    var todayObj = new Date(today + 'T00:00:00');
    var isPastDate = selectedDateObj < todayObj;
    
    // Add Meal button component (navigates directly to Log Food)
    var addMealButton = (
      <Button
        onClick={handleAddMealClick}
        variant="outline"
        className="w-full h-11 rounded-xl border-dashed"
        data-testid="add-meal-btn"
      >
        <Plus size={18} className="mr-2" />
        Add Meal
      </Button>
    );
    
    return (
      <div className="space-y-4">
        {/* Macro Progress */}
        {targetsAvailable ? (
          <div className="bg-card border border-border/40 rounded-xl p-4">
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Flame size={14} className="text-orange-500" />
                </div>
                <p className="font-bold">{Math.round(logged.calories)}</p>
                <p className="text-[10px] text-muted-foreground">/ {targets.calories}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Beef size={14} className="text-red-500" />
                </div>
                <p className="font-bold">{Math.round(logged.protein)}g</p>
                <p className="text-[10px] text-muted-foreground">/ {targets.protein}g</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Wheat size={14} className="text-amber-500" />
                </div>
                <p className="font-bold">{Math.round(logged.carbs)}g</p>
                <p className="text-[10px] text-muted-foreground">/ {targets.carbs}g</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Droplet size={14} className="text-blue-500" />
                </div>
                <p className="font-bold">{Math.round(logged.fat)}g</p>
                <p className="text-[10px] text-muted-foreground">/ {targets.fat}g</p>
              </div>
            </div>
          </div>
        ) : (
          <NutritionTargetsSetupPrompt compact />
        )}

        {/* Logged Meals Section */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Logged ({completed.length})
          </p>
          {completed.length > 0 ? (
            <div className="space-y-2">
              {completed.map(function(planItem) {
                // Falls back to the plan row (custom meals, AI logs, Spoonacular items) and
                // always keeps the row's saved image_url (AI private-photo marker).
                var mealData = mealFromPlanRow(planItem, dailyMealDetails[planItem.meal_id], 'Logged Food');
                return (
                  <MealCard
                    key={planItem.plan_id}
                    meal={mealData}
                    planMacros={planItem.macros}
                    mealType={planItem.meal_type}
                    planId={planItem.plan_id}
                    status={planItem.status}
                    showLoggedBadge={true}
                    onCardClick={function() { handleMealClick(planItem.meal_id, planItem.plan_id, planItem); }}
                    onUndo={function(e) { updateMealStatus(planItem.plan_id, 'pending', e); }}
                    onRemove={function(e) { removeMeal(planItem.plan_id, e); }}
                    isLoading={{
                      undo: isActionLoading(planItem.plan_id, 'undo'),
                      remove: isActionLoading(planItem.plan_id, 'remove')
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No meals logged yet</p>
          )}
          
          {/* Add Meal under Logged for PAST dates */}
          {isPastDate && addMealButton}
        </div>
        
        {/* Pending/Planned Meals Section */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Still Planned ({pending.length})
          </p>
          {pending.length > 0 ? (
            <div className="space-y-2">
              {pending.map(function(planItem) {
                var mealData = mealFromPlanRow(planItem, dailyMealDetails[planItem.meal_id], 'Planned Food');
                return (
                  <MealCard
                    key={planItem.plan_id}
                    meal={mealData}
                    planMacros={planItem.macros}
                    mealType={planItem.meal_type}
                    planId={planItem.plan_id}
                    status={planItem.status}
                    onCardClick={function() { handleMealClick(planItem.meal_id, planItem.plan_id, planItem); }}
                    onLog={function(e) { updateMealStatus(planItem.plan_id, 'completed', e); }}
                    onSkip={function(e) { updateMealStatus(planItem.plan_id, 'skipped', e); }}
                    onRemove={function(e) { removeMeal(planItem.plan_id, e); }}
                    isLoading={{
                      log: isActionLoading(planItem.plan_id, 'log'),
                      skip: isActionLoading(planItem.plan_id, 'skip'),
                      remove: isActionLoading(planItem.plan_id, 'remove')
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No meals planned</p>
          )}
          
          {/* Add Meal under Planned for TODAY and FUTURE dates */}
          {!isPastDate && addMealButton}
        </div>
      </div>
    );
  }

  // Render weekly view content
  function renderWeeklyView() {
    // Use currentWeekDates as SINGLE SOURCE OF TRUTH
    console.log('[Progress] renderWeeklyView - currentWeekDates:', currentWeekDates);
    console.log('[Progress] renderWeeklyView - weeklyData dates:', weeklyData.map(function(d) { return d.date; }));
    
    return (
      <div className="space-y-3">
        <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
          {/* Calendar Strip - pass EXPLICIT weekDates to ensure alignment */}
          <CalendarView
            viewMode="weekly"
            selectedDate={selectedDate}
            onDateSelect={handleCalendarDateSelect}
            renderDayContent={renderDayContent}
            showToggle={false}
            weekOffset={weekOffset}
            onWeekChange={setWeekOffset}
            weekDates={currentWeekDates}
            hideHeader={true}
          />
        </div>
        
        {/* Chart Section */}
        <div className="bg-card border border-border/40 rounded-xl p-3">
          {renderLineChart()}
        </div>
        
        {/* Weekly Summary */}
        {renderWeeklySummary()}
      </div>
    );
  }

  // Render monthly view content
  function renderMonthlyView() {
    var weeks = getWeeksInMonth(monthOffset);
    var safeIndex = Math.min(monthWeekIndex, weeks.length - 1);
    var currentWeek = weeks[safeIndex];
    
    var weekNumberLabel = currentWeek ? 'Week ' + currentWeek.weekNumber : '';
    var weekLabel = currentWeek ? 
      currentWeek.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + 
      ' – ' + 
      currentWeek.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    
    var canGoBack = monthWeekIndex > 0;
    var canGoForward = monthWeekIndex < weeks.length - 1;
    
    return (
      <div className="space-y-4">
        <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
          {/* Month Calendar - hideHeader removes inner navigation since top header handles it */}
          <CalendarView
            viewMode="monthly"
            selectedDate={today}
            onDateSelect={handleCalendarDateSelect}
            renderDayContent={renderDayContent}
            showToggle={false}
            monthOffset={monthOffset}
            onMonthChange={setMonthOffset}
            hideHeader={true}
          />
        </div>
        
        {/* Week Navigation for Chart */}
        <div className="flex items-center justify-between px-2">
          <button 
            onClick={function() { if (canGoBack) setMonthWeekIndex(monthWeekIndex - 1); }}
            disabled={!canGoBack}
            className={'p-2 rounded-full hover:bg-secondary transition-all ' + (!canGoBack ? 'opacity-30' : '')}
            data-testid="week-nav-prev"
          >
            <ChevronLeft size={18} />
          </button>
          
          <div className="text-center">
            <p className="font-semibold text-sm" data-testid="week-number-label">{weekNumberLabel}</p>
            <p className="text-[11px] text-muted-foreground">{weekLabel}</p>
          </div>
          
          <button 
            onClick={function() { if (canGoForward) setMonthWeekIndex(monthWeekIndex + 1); }}
            disabled={!canGoForward}
            className={'p-2 rounded-full hover:bg-secondary transition-all ' + (!canGoForward ? 'opacity-30' : '')}
            data-testid="week-nav-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        
        {/* Chart Section */}
        <div className="bg-card border border-border/40 rounded-xl p-3">
          {renderLineChart()}
        </div>
        
        {/* Weekly Summary */}
        {renderWeeklySummary()}
      </div>
    );
  }

  // Get formatted date range text based on view mode
  // USES currentWeekDates as SINGLE SOURCE OF TRUTH via shared utility
  function getDateRangeText() {
    if (viewMode === 'daily') {
      var dateObj = normalizeDate(selectedDate);
      return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
    } else if (viewMode === 'weekly') {
      // Use the SAME dates that feed the chart and calendar - via shared utility
      if (currentWeekDates.length === 0) {
        return 'Loading...';
      }
      console.log('[Progress] Header using dates - start:', currentWeekDates[0], 'end:', currentWeekDates[6]);
      return getWeekRangeText(currentWeekDates);
    } else if (viewMode === 'monthly') {
      // Monthly view: show full month name and year (e.g., "February 2026")
      var now = normalizeDate(new Date());
      var targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
      return targetMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  }

  // Navigation handlers
  function navigatePrev() {
    if (viewMode === 'daily') {
      var d = normalizeDate(selectedDate);
      d.setDate(d.getDate() - 1);
      setSelectedDate(formatDateString(d));
    } else if (viewMode === 'weekly') {
      setWeekOffset(weekOffset - 1);
    } else {
      setMonthOffset(monthOffset - 1);
    }
  }

  function navigateNext() {
    if (viewMode === 'daily') {
      var d = normalizeDate(selectedDate);
      d.setDate(d.getDate() + 1);
      setSelectedDate(formatDateString(d));
    } else if (viewMode === 'weekly') {
      setWeekOffset(weekOffset + 1);
    } else {
      setMonthOffset(monthOffset + 1);
    }
  }

  // Get return button text and visibility
  function getReturnButtonInfo() {
    if (viewMode === 'daily') {
      return { show: !isViewingToday, text: 'Return to Today', action: returnToToday };
    } else if (viewMode === 'weekly') {
      return { show: !isViewingThisWeek, text: 'Return to This Week', action: returnToThisWeek };
    } else {
      return { show: !isViewingThisMonth, text: 'Return to This Month', action: returnToThisMonth };
    }
  }

  // Get current period label
  function getCurrentPeriodLabel() {
    if (viewMode === 'daily' && isViewingToday) return 'Today';
    if (viewMode === 'weekly' && isViewingThisWeek) return 'This Week';
    if (viewMode === 'monthly' && isViewingThisMonth) return 'This Month';
    return null;
  }

  var returnInfo = getReturnButtonInfo();
  var currentLabel = getCurrentPeriodLabel();

  return (
    <div className="min-h-screen pb-bottom-nav bg-background" data-testid="progress-page">
      {/* Header - Matching Plan Section Layout */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        {/* px-4 pb-4 only: `header-safe-top` above provides the single top offset */}
        <div className="px-4 pb-4">
          {/* Title Row with View Toggle */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-heading font-bold text-xl">Progress</h1>
            
            {/* View Mode Toggle */}
            <div className="flex bg-secondary rounded-full p-1">
              <button
                onClick={function() { setViewMode('daily'); }}
                className={'px-3 py-1.5 rounded-full text-xs font-medium transition-all ' + 
                  (viewMode === 'daily' ? 'bg-accent text-white' : 'text-muted-foreground')}
                data-testid="view-daily"
              >
                Daily
              </button>
              <button
                onClick={function() { setViewMode('weekly'); }}
                className={'px-3 py-1.5 rounded-full text-xs font-medium transition-all ' + 
                  (viewMode === 'weekly' ? 'bg-accent text-white' : 'text-muted-foreground')}
                data-testid="view-weekly"
              >
                Weekly
              </button>
              <button
                onClick={function() { setViewMode('monthly'); }}
                className={'px-3 py-1.5 rounded-full text-xs font-medium transition-all ' + 
                  (viewMode === 'monthly' ? 'bg-accent text-white' : 'text-muted-foreground')}
                data-testid="view-monthly"
              >
                Monthly
              </button>
            </div>
          </div>

          {/* Date Navigation Row - Matching Plan Section */}
          <div className="flex items-center justify-between">
            <Button 
              onClick={navigatePrev} 
              variant="ghost" 
              size="sm" 
              className="rounded-full"
              data-testid="nav-prev"
            >
              <ChevronLeft size={18} />
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="text-center">
                <p className="font-heading font-semibold text-sm">{getDateRangeText()}</p>
                {currentLabel ? (
                  <p className="text-xs text-accent">{currentLabel}</p>
                ) : (
                  <button 
                    onClick={returnInfo.action}
                    className="text-xs text-accent hover:underline"
                    data-testid="return-btn"
                  >
                    {returnInfo.text}
                  </button>
                )}
              </div>
              
              {/* Calendar Icon for Date Picker */}
              <Button
                onClick={function() { setShowDatePicker(true); }}
                variant="ghost"
                size="icon"
                className="rounded-full h-8 w-8"
                data-testid="date-picker-btn"
              >
                <Calendar size={16} />
              </Button>
            </div>
            
            <Button 
              onClick={navigateNext} 
              variant="ghost" 
              size="sm" 
              className="rounded-full"
              data-testid="nav-next"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="progress-loading">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : loadError ? (
        <div className="p-4">
          <div
            className="mx-auto max-w-sm rounded-2xl border border-border/40 bg-card p-6 text-center"
            role="alert"
            data-testid="progress-load-error"
          >
            <p className="font-heading font-semibold text-base">We couldn&apos;t load your progress</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isTimeoutError(loadError)
                ? 'This is taking longer than usual. Check your connection and try again.'
                : 'Something went wrong. Check your connection and try again.'}
            </p>
            <Button
              onClick={function() { setReloadKey(function(k) { return k + 1; }); }}
              className="mt-4 rounded-full bg-accent text-white hover:bg-accent/90"
              data-testid="progress-retry-btn"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4">
          {viewMode === 'daily' && renderDailyView()}
          {viewMode === 'weekly' && renderWeeklyView()}
          {viewMode === 'monthly' && renderMonthlyView()}
          <button
            onClick={function() { navigate('/sources'); }}
            data-testid="progress-sources-link"
            className="block w-full text-center text-[11px] text-muted-foreground/70 hover:text-accent transition-colors mt-6"
          >
            Calorie &amp; macro estimates · Sources &amp; methodology
          </button>
        </div>
      )}

      {/* Date / week / month jump picker (rendered via portal) */}
      <JumpToPicker
        isOpen={showDatePicker}
        mode={viewMode === 'daily' ? 'date' : viewMode === 'weekly' ? 'week' : 'month'}
        selectedDate={selectedDate}
        weekOffset={weekOffset}
        monthOffset={monthOffset}
        onSelectDate={function(dateStr) { setSelectedDate(dateStr); }}
        onSelectWeek={function(off) { setWeekOffset(off); }}
        onSelectMonth={function(off) {
          setMonthOffset(off);
          setMonthWeekIndex(0);
        }}
        onClose={function() { setShowDatePicker(false); }}
      />

      <BottomNav />
    </div>
  );
}

export default Progress;
